import PdfPreview from "../ui/PdfPreview"
import { useEffect, useRef, useState } from "react"
import { SpinnerText } from "../ui/Spinner"
import {
  PowerOfAttorneyKind,
  UploadedPowerOfAttorney,
  downloadPowerOfAttorneyTemplate,
  downloadUploadedPowerOfAttorney,
  getPowerOfAttorneyStatus,
  uploadPowerOfAttorney
} from "../../services/powerOfAttorneyService"
import { formatApiError } from "../../utils/formatApiError"

const PATORG_LABELS = {
  SYNCED: "Imported into the PatOrg file",
  SKIPPED: "Not imported into PatOrg yet",
  ERROR: "PatOrg import failed"
}

type StepState = "pending" | "current" | "done"

// The server cannot know that the form was downloaded, so the browser
// remembers it for this application.
const downloadedKey = (applicationId: string) => `power-of-attorney-downloaded:${applicationId}`

function readDownloaded(applicationId: string) {
  try {
    return window.localStorage.getItem(downloadedKey(applicationId)) === "1"
  } catch {
    return false
  }
}

function rememberDownloaded(applicationId: string) {
  try {
    window.localStorage.setItem(downloadedKey(applicationId), "1")
  } catch {
    // The progress bar still moves for this visit
  }
}

function StepCircle({ value, state }: { value: number; state: StepState }) {
  const color =
    state === "done"
      ? "border-green-300 bg-green-100 text-green-700"
      : state === "current"
        ? "border-dantzBlue bg-white text-dantzBlue"
        : "border-gray-300 bg-white text-gray-400"

  return (
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors duration-500 ${color}`}
    >
      {state === "done" ? (
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        value
      )}
    </span>
  )
}

// Shown on a submitted application: the power of attorney (Vollmacht) to
// download, already filled in, and the place to upload the signed copy.
export default function PowerOfAttorneyPanel({
  kind,
  applicationId,
  isAdmin = false
}: {
  kind: PowerOfAttorneyKind
  applicationId: string
  isAdmin?: boolean
}) {
  const [uploaded, setUploaded] = useState<UploadedPowerOfAttorney | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloaded, setDownloaded] = useState(() => readDownloaded(applicationId))
  const [busy, setBusy] = useState<"template" | "upload" | "file" | null>(null)
  const [error, setError] = useState("")
  const fileInput = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let cancelled = false

    setDownloaded(readDownloaded(applicationId))

    getPowerOfAttorneyStatus(kind, applicationId)
      .then((status) => {
        if (!cancelled) setUploaded(status.uploaded)
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(formatApiError(loadError, "The power of attorney could not be loaded."))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [kind, applicationId])

  async function run(action: "template" | "upload" | "file", task: () => Promise<void>, fallback: string) {
    setBusy(action)
    setError("")

    try {
      await task()
    } catch (taskError) {
      setError(formatApiError(taskError, fallback))
    } finally {
      setBusy(null)
    }
  }

  function handleFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ""

    if (!file) return

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are allowed.")
      return
    }

    void run(
      "upload",
      async () => {
        const status = await uploadPowerOfAttorney(kind, applicationId, file)
        setUploaded(status.uploaded)
      },
      "The power of attorney could not be uploaded. Please try again."
    )
  }

  const downloadTemplate = () =>
    run(
      "template",
      async () => {
        await downloadPowerOfAttorneyTemplate(kind, applicationId)
        rememberDownloaded(applicationId)
        setDownloaded(true)
      },
      "The power of attorney could not be downloaded. Please try again."
    )

  const hasUpload = !loading && Boolean(uploaded)
  const firstDone = downloaded || hasUpload
  const firstState: StepState = firstDone ? "done" : "current"
  const secondState: StepState = hasUpload ? "done" : firstDone ? "current" : "pending"
  const linkClass = "text-dantzBlue underline disabled:opacity-50"
  const buttonClass =
    "mt-3 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"

  // One bar through the whole panel: step 1 to step 2 fills once the form is
  // downloaded, step 2 to the end of the panel once the signed copy is in.
  // The second column is as wide as its longest line, so the bar ends
  // exactly where that line ends.
  const firstBar = firstDone ? "100%" : "0%"
  const secondBar = hasUpload ? "100%" : "0%"

  return (
    <div className="mt-5 border-t border-gray-200 pt-5">
      <h4 className="text-sm font-semibold text-dantzBlue">Power of attorney</h4>

      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={handleFileChosen}
      />

      <div className="mt-4 grid gap-6 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <div className="flex items-center">
            <StepCircle value={1} state={firstState} />
            <div className="relative ml-3 hidden h-0.5 flex-1 overflow-hidden rounded-full bg-gray-200 sm:-mr-6 sm:block">
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-700 ease-out ${
                  hasUpload ? "bg-green-400" : "bg-blue-300"
                }`}
                style={{ width: firstBar }}
              />
            </div>
          </div>
          <p className="mt-2 text-sm font-medium text-gray-900">Download and sign</p>
          <p className="mt-1 text-sm text-gray-600">
            The form is already filled in with the details of this application. Print it, add place,
            date and signature.
          </p>
          <button
            type="button"
            disabled={busy !== null}
            onClick={downloadTemplate}
            className={buttonClass}
          >
            {busy === "template" ? <SpinnerText>Preparing...</SpinnerText> : downloaded || hasUpload ? "Download again" : "Download PDF"}
          </button>
        </div>

        <div>
          <div className="flex items-center">
            <StepCircle value={2} state={secondState} />
            <div className="relative ml-3 hidden h-0.5 flex-1 overflow-hidden rounded-full bg-gray-200 sm:block">
              <div
                className="absolute inset-y-0 left-0 bg-green-400 transition-all duration-700 ease-out"
                style={{ width: secondBar }}
              />
            </div>
          </div>
          <p className="mt-2 text-sm font-medium text-gray-900">Upload the signed copy</p>
          <p className="mt-1 text-sm text-gray-600">PDF, up to 20 MB.</p>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => fileInput.current?.click()}
            className={buttonClass}
          >
            {busy === "upload" ? <SpinnerText>Uploading...</SpinnerText> : hasUpload ? "Replace signed copy" : "Choose PDF"}
          </button>

          {hasUpload && uploaded && (
            <div className="mt-3 text-sm">
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600">
                <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">
                  Uploaded
                </span>
                <span>
                  {uploaded.fileName}, {new Date(uploaded.uploadedAt).toLocaleDateString("en-GB")}
                </span>
                <PdfPreview
                  endpoint={`/api/power-of-attorney/${kind}/${encodeURIComponent(applicationId)}/file`}
                  fileName={uploaded.fileName}
                  label="View"
                />
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() =>
                    run(
                      "file",
                      () => downloadUploadedPowerOfAttorney(kind, applicationId, uploaded.fileName),
                      "The file could not be downloaded. Please try again."
                    )
                  }
                  className={linkClass}
                >
                  Download
                </button>
              </p>
              {isAdmin && uploaded.patOrgStatus && (
                <p className="mt-1 whitespace-nowrap text-xs text-gray-500">
                  PatOrg: {PATORG_LABELS[uploaded.patOrgStatus]}
                  {uploaded.patOrgMessage ? ` (${uploaded.patOrgMessage})` : ""}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
